import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import { styles } from "./Contracts.styles";
import * as ContractAction from "../../../../stores/contracts/ContractAction";
import * as SubscriptionAction from "../../../../stores/subscriptions/SubscriptionAction";
import UniversalTable from "../../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../../components/CenteredContainer/CenteredContainer";
import { CsvExportButton, IColumnMapping } from "../../../components/CsvExport/CsvExportButton";
import { today } from "../../../../utilities/dateTimeUtils";
import RelationModel from "../../../../models/contract/RelationModel";
import SubscriptionModel from "../../../../models/subscription/SubscriptionModel";
import InformationProductModel from "../../../../models/contract/InformationProductModel";

export interface IRelationsViewProps extends WithStyles<typeof styles> {}

const RelationsViewPure: React.FunctionComponent<IRelationsViewProps> = ({ classes }: IRelationsViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(ContractAction.requestAllRelations());
        dispatch(ContractAction.requestAllInformationProducts());
        dispatch(SubscriptionAction.requestAllSubscriptionTypes());
    }, [dispatch]);

    const relationsFromState = useSelector(state => state.contracts.relations);
    const subscriptionTypesFromState = useSelector(state => state.subscriptions);
    const informationProductsFromState = useSelector(state => state.contracts.products);

    const isRequesting = useSelector(state =>
        selectRequesting(state, [
            ContractAction.REQUEST_ALL_RELATIONS,
            ContractAction.REQUEST_ALL_INFORMATION_PRODUCTS,
            SubscriptionAction.REQUEST_ALL_SUBSCRIPTIONS
        ])
    );

    let subscriptionTypes: SubscriptionModel[];
    if (subscriptionTypesFromState?.allSubscriptions) {
        subscriptionTypes = Object.values(subscriptionTypesFromState?.allSubscriptions);
    }

    let informationProducts: InformationProductModel[];
    if (informationProductsFromState) {
        informationProducts = Object.values(informationProductsFromState);
    }

    let relations: IUniversalTableData;
    if (relationsFromState) {
        relations = Object.values(relationsFromState)?.map((relation: RelationModel) => ({
            id: relation.id,
            product: { id: relation.productId, label: relation.productName },
            subscriptionType: { id: relation.subscriptionTypeId, label: relation.subscriptionName },
            subscriptionDescription: relation.subscriptionDescription
        }));
    }

    const handleEdit = (index, newData) => {
        const updatedRelation = {
            productId: newData.product?.id,
            subscriptionTypeId: newData.subscriptionType?.id
        };
        dispatch(ContractAction.updateRelation(relations[index].id, updatedRelation))
            .then(() => {
                dispatch(ContractAction.requestAllRelations());
                enqueueSnackbar("Relation edited", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Relation editing failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleAdd = data => {
        const newRelation = {
            productId: data.product?.id,
            subscriptionTypeId: data.subscriptionType?.id
        };
        dispatch(ContractAction.addRelation(newRelation))
            .then(() => {
                dispatch(ContractAction.requestAllRelations());
                enqueueSnackbar("Relation added", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Relation adding failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };
    const handleDelete = index => {
        dispatch(ContractAction.deleteRelation(relations[index].id))
            .then(() => {
                dispatch(ContractAction.requestAllRelations());
                enqueueSnackbar("Relation deleted", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Relation deletion failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const isRelationExist = rowData => {
        const index = relations.findIndex(
            r => r.product.id === rowData?.product?.id && r.subscriptionType.id === rowData?.subscriptionType?.id
        );

        // true when the combination is found and it's not the one that is being edited
        return index !== -1 && index !== rowData?.tableData?.id;
    };

    const columns: IUniversalTableColumn[] = [
        {
            title: "Information product",
            field: "product",
            type: "select",
            allValues: informationProducts?.map(p => ({ id: p.id, label: p.name })),
            validate: rowData => {
                if (rowData?.product === null || rowData?.product === undefined)
                    return { isValid: false, helperText: "Information product must not be empty" };
                return isRelationExist(rowData)
                    ? { isValid: false, helperText: "Selected combination already exist" }
                    : true;
            }
        },
        {
            title: "Subscription type",
            field: "subscriptionType",
            type: "select",
            allValues: subscriptionTypes?.map(p => ({ id: p.id, label: p.name })),
            validate: rowData => {
                if (rowData?.subscriptionType === null || rowData?.subscriptionType === undefined)
                    return { isValid: false, helperText: "Subscription type must not be empty" };
                return isRelationExist(rowData)
                    ? { isValid: false, helperText: "Selected combination already exist" }
                    : true;
            }
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "edit",
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newValue) => handleEdit(index, newValue),
                    scope: "contracts.write",
                },
                {
                    name: "add",
                    tooltip: "Add a relation",
                    onAddFinished: newValue => handleAdd(newValue),
                    scope: "contracts.write"
                },
                {
                    name: "delete",
                    tooltip: "Delete",
                    onDeleteFinished: newValue => handleDelete(newValue),
                    scope: "contracts.write"
                }
            ]
        }
    ];

    const exportMapping: IColumnMapping[] = [
        {
            label: "Information product",
            key: "product",
            mappingFunc: value => value.label
        },
        {
            label: "Subscription Type",
            key: "subscriptionType",
            mappingFunc: value => value.label
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    { title: "Contracts", href: "/admin/contracts" },
                    { title: "Manage Information Product vs. Subscription Type relations" }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h4">Manage Information Product vs. Subscription Type relations</Typography>
            </div>
            <div className={classes.exportButton}>
                <CsvExportButton
                    data={relations || []}
                    outputFilename={`information_product_vs_subscription_type_relations_${today().format(
                        "YYYYMMDD"
                    )}.csv`}
                    mappings={exportMapping}
                >
                    Export relations
                </CsvExportButton>
            </div>
            <UniversalTable columns={columns} data={relations} isLoading={isRequesting} searchable />
        </CenteredContainer>
    );
};

export const RelationsView = withStyles(styles)(RelationsViewPure);
